import { Bell, ClipboardList, ShieldCheck } from 'lucide-react';
import { RealScreen } from '@/components/marketing/roleKit';

/**
 * Parents Real Screen 1: visibility. Parent permissions are per link: the student decides whether a
 * parent sees sessions and joins tutor chat (docs/ROLES.md, Parent delegation). The surface shows both sides.
 */
const SESSIONS = [
  { when: 'Tomorrow · 3:00 PM', what: 'Algebra' },
  { when: 'Friday · 4:00 PM', what: 'SAT Math' },
] as const;

function Toggle({ label, on }: { label: string; on: boolean }) {
  return (
    <li className="flex items-center justify-between gap-3 py-2">
      <span className="text-sm">{label}</span>
      <span
        className={`flex h-5 w-9 shrink-0 items-center rounded-full p-0.5 ${on ? 'justify-end bg-emerald-600' : 'justify-start bg-muted-foreground/30'}`}
        role="img"
        aria-label={on ? 'On' : 'Off'}
      >
        <span className="h-4 w-4 rounded-full bg-white" />
      </span>
    </li>
  );
}

export function ParentVisibilitySection() {
  return (
    <RealScreen
      id="section-parent-visibility"
      audience="parents"
      layout="rail-right"
      heading="See what's booked, without asking"
      lede="Once your student links your account, their sessions appear in your dashboard. They decide how much you see."
      callouts={[
        { icon: ShieldCheck, title: 'Your student sets the limits', body: 'Permissions are per link. They choose whether you see sessions and join tutor chat, and they can revoke the link.' },
        { icon: Bell, title: 'Alerts you control', body: 'Booked, completed and cancelled alerts are on by default. You choose which ones to keep.' },
        { icon: ClipboardList, title: 'Acting for them is logged', body: 'Anything you do on their behalf is recorded, and they are told.' },
      ]}
      caption="Example parent dashboard, following one linked student."
    >
      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-xl border bg-background p-4">
          <p className="text-sm font-semibold">Upcoming sessions</p>
          <p className="text-xs text-muted-foreground">Alex M.</p>
          <ul className="mt-3 space-y-2">
            {SESSIONS.map((s) => (
              <li key={s.when} className="flex items-center justify-between rounded-md border bg-muted/30 px-3 py-2">
                <div>
                  <p className="text-xs text-muted-foreground">{s.when}</p>
                  <p className="text-sm font-medium">{s.what}</p>
                </div>
                <span className="text-xs font-medium text-emerald-700 dark:text-emerald-400">Confirmed</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-xl border bg-background p-4">
          <p className="text-sm font-semibold">Set by Alex</p>
          <ul className="mt-1 divide-y">
            <Toggle label="Sessions visible to you" on />
            <Toggle label="You join the tutor chat" on />
          </ul>
          <p className="mt-4 text-sm font-semibold">Your alerts</p>
          <ul className="mt-1 divide-y">
            <Toggle label="Session booked" on />
            <Toggle label="Session completed" on />
            <Toggle label="New chat message" on={false} />
          </ul>
        </div>
      </div>
    </RealScreen>
  );
}
